import { useCallback, useEffect, useRef, useState } from "react";
import { fmtMYT } from "@/lib/myt";
import { Bitcoin, Coins, CreditCard, Lock, Send, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtCoins } from "@/components/shop/PurchaseModal";

const KIND_LABELS = {
  topup: "Top-Up",
  purchase: "Purchase",
  admin_credit: "Admin Credit",
  admin_debit: "Admin Debit",
};

const CURRENCIES = ["USD", "MYR", "SGD", "IDR"];

// Coin Top-Up via Admin — Telegram deep link (single source for the CTA/helper text).
const TELEGRAM_ADMIN_LINK = "https://t.me/m/VZVDTxL9NDZl";

const ORDER_STATUS_STYLES = {
  pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  approved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
  cancelled: "text-[#94A3B8] bg-[#94A3B8]/10 border-[#94A3B8]/25",
  expired: "text-[#94A3B8] bg-[#94A3B8]/10 border-[#94A3B8]/25",
};

// visible-but-locked providers — architecture preserved for future activation
const LOCKED_METHODS = [
  { key: "stripe", label: "Buy via Stripe", icon: CreditCard, accent: "text-[#635BFF]", ring: "border-[#635BFF]/25" },
  { key: "crypto", label: "Buy via Crypto", icon: Bitcoin, accent: "text-[#F7931A]", ring: "border-[#F7931A]/25" },
  { key: "toyyibpay", label: "Buy via ToyyibPay", icon: Wallet, accent: "text-emerald-400", ring: "border-emerald-400/25" },
];

// Local Depositor is the only active client-facing purchase method; Admin top-up UI is locked.
const ADMIN_TOPUP_ENABLED = false;

const fmtMoney = (currency, amount) =>
  `${currency} ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MemberCoins() {
  const [data, setData] = useState(null);
  const [rates, setRates] = useState(null);
  const [orders, setOrders] = useState(null);
  const [currency, setCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submitLock = useRef(false);
  const [ld, setLd] = useState(null);
  const [ldCoins, setLdCoins] = useState("");
  const [ldRequests, setLdRequests] = useState([]);
  const [ldBusy, setLdBusy] = useState(false);
  const ldLock = useRef(false);

  const loadBalance = useCallback(() => {
    api.get("/member/coins").then((res) => setData(res.data)).catch(() => setData({ balance_minor: 0, transactions: [] }));
  }, []);
  const loadOrders = useCallback(() => {
    api.get("/member/coin-orders").then((res) => setOrders(res.data.orders || [])).catch(() => setOrders([]));
  }, []);
  const loadRates = useCallback(() => {
    api.get("/member/coin-orders/rates").then((res) => setRates(res.data)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    loadBalance();
    loadOrders();
    loadRates();
  }, [loadBalance, loadOrders, loadRates]);

  const loadLdRequests = useCallback(() => {
    api.get("/member/local-depositor/requests").then((res) => setLdRequests(res.data.requests || [])).catch(() => setLdRequests([]));
  }, []);

  useEffect(() => {
    api.get("/member/local-depositor").then((res) => setLd(res.data)).catch(() => setLd({ available: false }));
    loadLdRequests();
  }, [loadLdRequests]);

  const ldCoinsNum = Number(ldCoins);
  const ldValid = ld?.available && ldCoins !== "" && Number.isInteger(ldCoinsNum) && ldCoinsNum >= (ld?.min_coins || 15);

  const createLdRequest = async () => {
    if (!ldValid || ldLock.current) return;
    ldLock.current = true;
    setLdBusy(true);
    try {
      const res = await api.post("/member/local-depositor/request", {
        amount: ldCoinsNum,
        client_token: (window.crypto?.randomUUID?.() || String(Date.now())),
      });
      toast.success(`Request created — reference ${res.data.request?.reference}. Contact the Local Depositor to pay.`);
      const link = res.data.link || ld.link;
      if (link) window.open(link, "_blank", "noopener,noreferrer");
      setLdCoins("");
      loadLdRequests();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLdBusy(false);
      ldLock.current = false;
    }
  };

  const coinsNum = Number(amount);
  const coinsValid = amount !== "" && Number.isInteger(coinsNum) && coinsNum >= 15;
  const rate = rates && currency ? Number(rates.rates?.[currency]) : null;
  const converted = coinsValid && rate ? Math.round(coinsNum * rate * 100) / 100 : null;

  const createOrder = async (e) => {
    e.preventDefault();
    setError("");
    if (!currency) { setError("Please select a payment currency first."); return; }
    if (amount === "" || !Number.isInteger(coinsNum)) { setError("Enter a whole number of Coins (no decimals)."); return; }
    if (coinsNum < 15) { setError("Minimum top-up is 15 Coins (USD 15)."); return; }
    if (submitLock.current) return;
    submitLock.current = true;
    setBusy(true);
    try {
      const res = await api.post("/member/coin-orders", {
        coins: coinsNum,
        currency,
        client_token: (window.crypto?.randomUUID?.() || String(Date.now())),
      });
      const msg = res.data.telegram_message;
      const link = res.data.telegram_link;
      // Order saved successfully — copy details (best-effort, must NOT block/replace the redirect)...
      try {
        await navigator.clipboard.writeText(msg);
        toast.success("Order created — details copied. Redirecting to Telegram…");
      } catch {
        toast.success("Order created — redirecting to Telegram…");
      }
      // ...then redirect the client directly to the admin Telegram chat.
      window.location.href = link;
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
      submitLock.current = false;
    }
  };

  const cancelOrder = async (id) => {
    try {
      await api.post(`/member/coin-orders/${id}/cancel`);
      toast.success("Order cancelled.");
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="member-coins-page">
      <div>
        <p className="eyebrow-label mb-2">Coins Wallet</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">My Coins</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          1 Coin = USD 1. Coins never expire. Top up via the admin and your balance is credited the moment payment is confirmed.
        </p>
      </div>

      {/* Balance */}
      <div className="card-lux p-8" data-testid="coin-balance-card">
        <p className="eyebrow-label mb-4">Current Balance</p>
        <p className="font-mono text-4xl font-bold text-[#D4AF37]" data-testid="coin-balance">
          {data ? fmtCoins(data.balance_minor) : "—"}
        </p>
        <p className="mt-2 text-xs text-[#64748B]" data-testid="coin-usd-equivalent">
          {data ? `≈ USD ${(data.balance_minor / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""}
        </p>
      </div>

      {/* Payment method selector */}
      <div>
        <p className="eyebrow-label mb-3">Choose Payment Method</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="coin-methods">
          {/* Buy via Admin — LOCKED / COMING SOON */}
          <div
            className="relative rounded-2xl border border-[#2C3140] bg-[#0B0C10]/60 p-4 flex flex-col gap-2 opacity-60"
            data-testid="coin-method-admin"
            aria-disabled="true"
          >
            <span className="inline-flex items-center gap-2 text-[#E5C158]">
              <Send className="h-5 w-5" />
              <span className="font-heading text-sm font-bold text-white/70">Buy via Admin</span>
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase text-[#94A3B8] border border-[#2C3140] rounded-full px-2 py-0.5 w-fit" data-testid="coin-method-admin-locked">
              <Lock className="h-3 w-3" /> Coming Soon
            </span>
            <p className="text-[11px] text-[#64748B]">Not available right now — use Buy via Local Depositor.</p>
          </div>

          {ld?.available && (
            <div className="relative rounded-2xl border-2 border-emerald-400/60 bg-emerald-400/5 p-4 flex flex-col gap-2" data-testid="coin-method-local-depositor">
              <span className="inline-flex items-center gap-2 text-emerald-400"><Wallet className="h-5 w-5" /><span className="font-heading text-sm font-bold text-white">Buy via Local Depositor</span></span>
              <span className="text-[10px] font-mono tracking-widest uppercase text-[#050506] bg-emerald-400 rounded-full px-2 py-0.5 w-fit">Active</span>
              <p className="text-[11px] text-[#94A3B8]">Create a Buy request, pay in MYR to our Local Depositor, then tap Contact Local Depositor to send your receipt.</p>
            </div>
          )}
          {LOCKED_METHODS.map((m) => (
            <div
              key={m.key}
              className={`relative rounded-2xl border ${m.ring} bg-[#0B0C10]/60 p-4 flex flex-col gap-2 opacity-60`}
              data-testid={`coin-method-${m.key}`}
              aria-disabled="true"
            >
              <span className={`inline-flex items-center gap-2 ${m.accent}`}>
                <m.icon className="h-5 w-5" />
                <span className="font-heading text-sm font-bold text-white/70">{m.label}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase text-[#94A3B8] border border-[#2C3140] rounded-full px-2 py-0.5 w-fit" data-testid={`coin-method-${m.key}-locked`}>
                <Lock className="h-3 w-3" /> Coming Soon
              </span>
              <p className="text-[11px] text-[#64748B]">Not available yet — stay tuned.</p>
            </div>
          ))}
        </div>
      </div>

      {ld?.available && (
        <div className="card-lux p-6 sm:p-8 space-y-5" data-testid="coin-local-depositor">
          <p className="eyebrow-label">Buy via Local Depositor (MYR)</p>
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Coins (whole number) — minimum {ld.min_coins}</label>
            <input type="number" min={ld.min_coins} step="1" className="input-lux font-mono" value={ldCoins} onChange={(e) => setLdCoins(e.target.value)} placeholder="e.g. 100" data-testid="ld-buy-amount-input" />
            <p className="mt-1.5 text-[10px] text-[#64748B]">1 Coin = USD 1 = MYR {ld.rate_myr.toFixed(2)}. Minimum {ld.min_coins} coins (RM{(ld.min_coins * ld.rate_myr).toFixed(2)}).</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-[#222530] p-4 space-y-1.5" data-testid="ld-bank-details">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Transfer To</p>
              <p className="text-white">{ld.bank_name}</p>
              <p className="text-[#94A3B8]">{ld.account_holder_name}</p>
              <p className="font-mono text-white break-all">{ld.bank_account_number}</p>
            </div>
            <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 flex flex-col justify-center">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">You Pay</p>
              <p className="text-2xl font-bold text-[#D4AF37] font-mono" data-testid="ld-myr-amount">RM {ldValid ? (ldCoinsNum * ld.rate_myr).toFixed(2) : "0.00"}</p>
              <p className="text-[11px] text-[#94A3B8] mt-0.5">for {ldValid ? ldCoinsNum : 0} COINS</p>
            </div>
          </div>
          <p className="text-[11px] text-[#64748B]">Create your Buy request, then pay the MYR amount to the Local Depositor and send your <span className="text-[#94A3B8]">payment receipt</span> via the contact link. Your COINS are credited once the Local Depositor approves your request.</p>
          <div className="flex flex-wrap gap-3">
            <button onClick={createLdRequest} disabled={!ldValid || ldBusy} className="btn-gold px-6 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="ld-create-request-button">
              <Send className="h-4 w-4" /> {ldBusy ? "Creating request…" : "Create Buy Request"}
            </button>
            {ld.link && (
              <a href={ld.link} target="_blank" rel="noreferrer" className="btn-gold-outline px-6 py-2.5 text-sm inline-flex items-center gap-2" data-testid="ld-contact-button">
                <Send className="h-4 w-4" /> Contact Local Depositor
              </a>
            )}
          </div>

          {/* My Local Depositor requests */}
          <div className="pt-2">
            <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-2">My Local Depositor Requests</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ld-requests-table">
                <thead>
                  <tr className="border-b border-[#222530] text-left">
                    <th className="px-3 py-2 text-xs font-medium text-[#64748B]">Reference</th>
                    <th className="px-3 py-2 text-xs font-medium text-[#64748B] text-right">Coins</th>
                    <th className="px-3 py-2 text-xs font-medium text-[#64748B] text-right">MYR</th>
                    <th className="px-3 py-2 text-xs font-medium text-[#64748B]">Status</th>
                    <th className="px-3 py-2 text-xs font-medium text-[#64748B] hidden sm:table-cell">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#222530]">
                  {ldRequests.length === 0 ? (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-[#64748B]" data-testid="ld-requests-empty">No requests yet.</td></tr>
                  ) : ldRequests.map((r) => (
                    <tr key={r.id} data-testid={`ld-request-row-${r.reference}`}>
                      <td className="px-3 py-2 font-mono text-white whitespace-nowrap">{r.reference}</td>
                      <td className="px-3 py-2 font-mono text-right text-white">{r.coins}</td>
                      <td className="px-3 py-2 font-mono text-right text-[#94A3B8]">RM {Number(r.myr || 0).toFixed(2)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${r.status === "completed" ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" : "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30"}`} data-testid={`ld-request-status-${r.reference}`}>
                          {r.status === "completed" ? "Approved" : "Pending"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#94A3B8] whitespace-nowrap hidden sm:table-cell">{r.created_at ? fmtMYT(r.created_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Buy via Admin form — LOCKED: gated so members cannot initiate (backend untouched) */}
      {ADMIN_TOPUP_ENABLED && (
      <div className="card-lux p-6 sm:p-8" data-testid="coin-admin-topup">
        <div className="flex items-center gap-2 mb-1">
          <Send className="h-4 w-4 text-[#E5C158]" />
          <p className="font-heading text-lg font-bold text-white">Top Up via Admin</p>
        </div>
        <p className="text-xs text-[#64748B] mb-6">
          Contact the PlusMaker admin on{" "}
          <a href={TELEGRAM_ADMIN_LINK} target="_blank" rel="noopener noreferrer" className="text-[#E5C158] hover:underline" data-testid="coin-admin-telegram-link">Telegram</a>{" "}
          to complete payment. Your order details are copied automatically.
        </p>

        {!rates && (
          <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3 mb-4" data-testid="coin-rates-unavailable">
            Exchange rates are temporarily unavailable. Please refresh in a moment.
          </p>
        )}

        <form onSubmit={createOrder} className="space-y-5" data-testid="coin-topup-form">
          {/* Currency */}
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Payment Currency <span className="text-red-400">*</span></label>
            <div className="flex flex-wrap gap-2" data-testid="coin-currency-selector">
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className={`text-xs font-mono tracking-widest uppercase rounded-full px-4 py-2 border transition-colors ${
                    currency === c
                      ? "text-[#050506] bg-[#E5C158] border-[#E5C158]"
                      : "text-[#94A3B8] border-[#222530] hover:border-[#D4AF37]/40"
                  }`}
                  data-testid={`coin-currency-${c}`}
                  aria-pressed={currency === c}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Coin amount */}
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Coins (whole number) — minimum 15</label>
            <input
              type="number"
              min="15"
              step="1"
              className="input-lux font-mono"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 100"
              required
              data-testid="coin-topup-amount-input"
            />
            <p className="mt-1.5 text-[10px] text-[#64748B]">1 Coin = USD 1. Whole numbers only — no decimals.</p>
          </div>

          {/* Live quote preview */}
          {currency && coinsValid && rate && (
            <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-5 py-4 space-y-1.5 text-sm" data-testid="coin-quote-preview">
              <div className="flex justify-between"><span className="text-[#94A3B8]">Coins</span><span className="font-mono text-white">{coinsNum}</span></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Base value</span><span className="font-mono text-white">USD {coinsNum}</span></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Payment currency</span><span className="font-mono text-white">{currency}</span></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Exchange rate</span><span className="font-mono text-white">1 USD = {rate.toLocaleString("en-US", { maximumFractionDigits: 4 })}</span></div>
              <div className="flex justify-between border-t border-[#D4AF37]/20 pt-2 mt-1"><span className="text-[#E5C158] font-medium">Amount to pay</span><span className="font-mono text-[#E5C158] font-bold" data-testid="coin-quote-amount">{fmtMoney(currency, converted)}</span></div>
              <p className="text-[10px] text-[#64748B] pt-1">Quote valid for 15 minutes. The rate is locked when you confirm the order.</p>
            </div>
          )}

          {error && <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="coin-topup-error">{error}</p>}

          <button
            type="submit"
            disabled={busy || !rates}
            className="btn-gold px-8 py-3 text-sm disabled:opacity-60 flex items-center gap-2"
            data-testid="coin-topup-submit"
          >
            <Send className="h-4 w-4" /> {busy ? "Creating order..." : "Confirm & Open Telegram"}
          </button>
        </form>
      </div>
      )}

      {/* My top-up orders */}
      <div className="card-lux overflow-hidden" data-testid="coin-orders-section">
        <div className="px-6 pt-6"><p className="eyebrow-label">My Top-Up Orders</p></div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="coin-orders-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Reference</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Coins</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Amount</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Created</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {!orders ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]">Loading...</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#64748B]" data-testid="coin-orders-empty">No top-up orders yet.</td></tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`coin-order-${o.reference}`}>
                    <td className="px-6 py-3 font-mono text-white whitespace-nowrap">{o.reference}</td>
                    <td className="px-6 py-3 font-mono text-right text-white">{o.coin_quantity}</td>
                    <td className="px-6 py-3 font-mono text-right text-[#94A3B8]">{fmtMoney(o.currency, o.converted_amount)}</td>
                    <td className="px-6 py-3">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${ORDER_STATUS_STYLES[o.status] || ORDER_STATUS_STYLES.pending}`} data-testid={`coin-order-status-${o.reference}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-[#94A3B8] whitespace-nowrap hidden md:table-cell">{o.created_at ? fmtMYT(o.created_at) : "—"}</td>
                    <td className="px-6 py-3 text-right">
                      {o.status === "pending" && (
                        <button onClick={() => cancelOrder(o.id)} className="text-[11px] font-mono uppercase tracking-widest text-[#94A3B8] hover:text-red-400 transition-colors" data-testid={`coin-order-cancel-${o.reference}`}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Coin ledger history */}
      <div className="card-lux overflow-hidden" data-testid="coin-history-section">
        <div className="px-6 pt-6"><p className="eyebrow-label">Transaction History</p></div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="coin-history-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">When</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Type</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Amount</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Balance After</th>
                <th className="px-6 py-4 text-xs font-medium text-[#64748B] tracking-wide">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {!data ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]">Loading...</td></tr>
              ) : data.transactions.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#64748B]" data-testid="coin-history-empty">No coin transactions yet.</td></tr>
              ) : (
                data.transactions.map((t) => (
                  <tr key={t.id} className="hover:bg-[#0B0C10]/60 transition-colors" data-testid={`coin-tx-${t.id.slice(-6)}`}>
                    <td className="px-6 py-3 text-[#94A3B8] whitespace-nowrap">{t.created_at ? fmtMYT(t.created_at) : "—"}</td>
                    <td className="px-6 py-3">
                      <span className="text-[10px] font-mono tracking-widest uppercase rounded-full px-2 py-0.5 border border-[#222530] text-[#94A3B8]">{KIND_LABELS[t.kind] || t.kind}</span>
                    </td>
                    <td className={`px-6 py-3 font-mono text-right ${t.amount_minor >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {t.amount_minor >= 0 ? "+" : ""}{fmtCoins(t.amount_minor)}
                    </td>
                    <td className="px-6 py-3 font-mono text-white text-right">{fmtCoins(t.balance_after_minor)}</td>
                    <td className="px-6 py-3 text-xs text-[#94A3B8] max-w-[240px] truncate">{t.reason}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
